import { inject, Injectable } from '@angular/core';
import { FiscalPayment, ItalianFiscalPrinterDriver, ItPrinterConfigurationOptions, PrintFreeOptions } from 'src/app/shared/model/it-fiscal-printer.model';
import { Printers, SalesDocuments, Sales, SalesItems, Vat, SalesPriceChanges, SalesItemsPriceChanges } from 'tilby-models';
import { DITRON_SYSTEM_CMDS } from './ditron-commands-library';
import { fiscalUtils } from 'app/ajs-upgraded-providers';
import { APEX_STRING_OPERAND, barcodeTypeCodesDitronPrinter, defaultBarcodeTypeDitron, DITRON_PAYMENT_MAP, DitronCommandResult, DitronPayment, DitronPrinterCommand, errorDitronPrinter, InstructionName, maxLengthDesc, maxLengthMsg, OperandKey, POSITIONAL_INSTRUCTIONS, RealCommand, RESPONSE_MAP, TextOptions } from './ditron-protocol-schema';
import { padCenter, truncateString } from 'src/app/shared/string-utils';
import moment from 'moment-timezone';
import { SalePrintingUtilsService } from 'src/app/features/cashregister';

/**
 *  Tilby Ditron Driver
 *
 *  Usage:
 *  DitronDriverService.setup() and after call public methods:
 *
 *  - isReachable
 *  - getPrinterStatus
 *  - autoConfigurePrinter
 *  - printFiscalReceipt
 *  - printCourtesyReceipt
 *  - printNonFiscal
 *  - printFreeNonFiscal
 *  - dailyClosing
 *  - dailyRead
 *  - openCashDrawer
 *  - displayText
 *
 */

const cleanUpSpecialChars = (str?: string): string => {
  if (!str) return '';

  return str
    // Sostituzione Euro (globale)
    .replace(/€/g, "E")
    
    // Gestione Apostrofo: lo trasformiamo in un accento retroverso (backtick) che non rompe il protocollo
    .replace(/'/g, "`") 
    
    // Normalizzazione Accenti (Sostituisce à con a, è con e, ecc.)
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")

    // Rimozione caratteri non stampabili o pericolosi
    .replace(/[^\x20-\x7E\n\r]/g, "*") 
    
    // Rimozione caratteri che Ditron usa come separatori (es: ;)
    .replace(/[;]/g, ".");
};

const wrapText = (text: string, maxLength: number): string[] => {
  // Gestione righe vuote
  if(text === ' ') return [text];

  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  words.forEach(word => {
    // Se la parola singola è più lunga del limite, dobbiamo tagliarla per forza
    if (word.length > maxLength) {
      if (currentLine.length > 0) lines.push(currentLine.trim());
      
      // Spezza la parola lunga in pezzi da maxLength
      const chunks = word.match(new RegExp(`.{1,${maxLength}}`, 'g')) || [];
      chunks.forEach((chunk, i) => {
        if (i < chunks.length - 1) lines.push(chunk);
        else currentLine = chunk + ' ';
      });
    } 
    // Se la parola ci sta nella riga corrente
    else if ((currentLine + word).length <= maxLength) {
      currentLine += word + ' ';
    } 
    // Altrimenti chiudiamo la riga e ne iniziamo una nuova
    else {
      lines.push(currentLine.trim());
      currentLine = word + ' ';
    }
  });

  if (currentLine.trim().length > 0) {
    lines.push(currentLine.trim());
  }

  return lines;
};

const addTextLine = (
  cmd: DitronPrinterCommand[], 
  text: string,
  options: TextOptions = { wrap: false, maxLength: maxLengthDesc }
) => {
  if (!text) return;

  const cleanedText = text === ' ' ? text: cleanUpSpecialChars(text).trim();
  const limit = options.maxLength || maxLengthDesc;

  if (options.wrap) {
    const wrappedLines = wrapText(cleanedText, limit);

    wrappedLines.forEach(line => {
      cmd.push({
        type: 'COMMAND',
        instruction: 'PRMSG',
        operands: { RIGA: line }
      });
    });
  } else {
    cmd.push({
      type: 'COMMAND',
      instruction: 'PRMSG',
      operands: { RIGA: cleanedText.substring(0, limit) }
    });
  }
}

const addAttachmentLine = (
  cmd: DitronPrinterCommand[], 
  text: string,
  options: TextOptions = { wrap: true, maxLength: maxLengthMsg }
) => {
  if (!text) return;

  const cleanedText = text === ' ' ? text: cleanUpSpecialChars(text).trim();
  const limit = options.maxLength || maxLengthMsg;

  if (options.wrap) {
    const wrappedLines = wrapText(cleanedText, limit);

    wrappedLines.forEach(line => {
      cmd.push({
        type: 'COMMAND',
        instruction: 'ALLEG',
        operands: { RIGA: line }
      });
    });
  } else {
    cmd.push({
      type: 'COMMAND',
      instruction: 'ALLEG',
      operands: { RIGA: cleanedText.substring(0, limit) }
    });
  }
}

const addLineNoFiscal = (
  cmd: DitronPrinterCommand[], 
  text: string,
  options: TextOptions = { wrap: false, maxLength: maxLengthMsg }
) => {
  if (!text) return;

  const cleanedText = text === ' ' ? text: cleanUpSpecialChars(text).trim();
  const limit = options.maxLength || maxLengthMsg;

  if (options.wrap) {
    const wrappedLines = wrapText(cleanedText, limit);

    wrappedLines.forEach(line => {
      cmd.push({
        type: 'COMMAND',
        instruction: 'NOFIS',
        operands: { RIGA: line }
      });
    });
  } else {
    cmd.push({
      type: 'COMMAND',
      instruction: 'NOFIS',
      operands: { RIGA: cleanedText }
    });
  }
}

const addBarcode = (
  cmd: DitronPrinterCommand[], 
  barcodeId: string,
  isFiscalReceipt: boolean, 
  barcodeType?: string
) => {
  if (!barcodeId) return;
  const cleanedText = barcodeId.trim();

  cmd.push({
    type: 'COMMAND',
    instruction: isFiscalReceipt? 'ALLEG': 'NOFIS',
    operands: {
      RIGA: cleanedText,
      BARCODE: barcodeType || defaultBarcodeTypeDitron
    }
  });
}

const activeCommands = (command: DitronPrinterCommand[]) => command.filter((c): c is RealCommand => c.type === 'COMMAND');

const findResultByCodice = (
  interpretedResults: DitronCommandResult[], 
  command: DitronPrinterCommand[], 
  codice: number
) => 
  interpretedResults.find((r, index) => {
    const cmd = activeCommands(command)[index];
    return cmd.instruction === 'INFO' && (cmd.operands as any).CODICE === codice;
  });

interface VoidRefundInterface {
  hasVoid: boolean,
  hasRefund: boolean
}

@Injectable({
  providedIn: 'root'
})
export class DitronDriverService implements ItalianFiscalPrinterDriver {

  private readonly fiscalUtilsService = inject(fiscalUtils);
  private readonly salePrintingUtilsService = inject(SalePrintingUtilsService);

	private printer!: Printers;
	private options: any = null;
  private matricola: string = '';

  constructor() {}

  /**
   * @description Trasforma l'array di oggetti nella stringa finale per l'hardware.
   * @param lines
   */
  private buildDitronPrinterCommand(lines: DitronPrinterCommand[]): string {
    const formattedLines = lines
      .map((line) => {
        // Caso A: Solo commento
        if (line.type === "COMMENT_ONLY") {
          return `; ${line.text}`;
        }

        // Caso B: Comando
        let cmdString = line.instruction.toUpperCase() as InstructionName;

        // Formattazione operandi (se esistono)
        if (line.operands && Object.keys(line.operands).length > 0) {
          const ops = Object.entries(line.operands)
            .map(([key, val]) => {
              const upperKey = key.toUpperCase() as OperandKey;
              let finalVal = val;

              if (POSITIONAL_INSTRUCTIONS.includes(cmdString)) {
                return val.toString();
              }

              if (APEX_STRING_OPERAND.includes(upperKey) && typeof val === "string") {
                const escaped = val.replace(/'/g, "''");
                finalVal = `'${escaped}'`;
              }

              if(finalVal == '') {
                return upperKey;
              }

              return `${upperKey}=${finalVal}`;
            })
            .join(", ");
          cmdString += ` ${ops}`;
        }

        // Gestione Commento
        // const finalComment = line.comment;

        return `${cmdString}`;
      })
    return [...formattedLines, ""].join("\n");
  }

  private parseDitronResponse(commands: DitronPrinterCommand[], rawResponse: string): DitronCommandResult[] {
    const responseLines = rawResponse.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);

    const activeCmd = activeCommands(commands);

    return activeCmd.map((cmd, index) => {
      const lineRes = responseLines[index] || "";

      const result: DitronCommandResult = {
        instruction: cmd.instruction,
        rawResponse: lineRes,
        status: 'SUCCESS',
        message: ''
      };

      // CASO A: ERRORE
      if (lineRes.includes("ERRORE")) {
        result.status = 'ERROR';
        const errorMatch = lineRes.match(/ERRORE\s+(\d+\/\d+)/);
        result.errorCode = errorMatch ? errorMatch[1] : "UNKNOWN";
        const defaultMessage = `Errore hardware rilevato: ${result.errorCode}`;
        result.message = errorMatch ? errorDitronPrinter[errorMatch[1]] || defaultMessage : defaultMessage;
      }
      // CASO B: OK successo
      else if (lineRes === "OK.") {
        result.status = 'SUCCESS';
        result.message = "Operazione completata";
      }
      // CASO C: Codice numerico (Stato, Info)
      else {
        result.status = 'INFO';
        const res = lineRes;

        if (cmd.instruction === 'INFO') {
          const codiceInviato = cmd.operands?.CODICE?.toString();

          switch (codiceInviato) {
            case '1': // Lettura MATRICOLA (es: 2CITP990345)
              result.data = { serial: res };
              result.message = `Matricola: ${res}`;
              break;

            case '7': // Lettura DATA/ORA (es: 23/07/26 11:55)
              result.data = { date: res };
              result.message = `Data: ${res}`;
              break;

            case '10': // Lettura INDICI (es: 00170001)
              if (res.length >= 8) {
                const z = parseInt(res.substring(0, 4));
                const n = parseInt(res.substring(4, 8));
                result.data = { zNumber: z, docNumber: n };
                result.message = `Azzeramento: ${z}, Progr: ${n}`;
              }
              break;

            case '19': // Lettura stato RT
              result.data = { status: res };
              result.message = `Stato: ${res} - ${RESPONSE_MAP['INFO']?.[res]}`;
              break;

            default:
              // Gestione standard tramite RESPONSE_MAP (es. per CODICE=19 o altri)
              const instructionMap = RESPONSE_MAP[cmd.instruction];
              const translation = instructionMap ? instructionMap[res] : null;
              result.message = translation || `Codice non documentato per ${cmd.instruction} con codice (${codiceInviato}): ${res}`;
          }
        } else {
          result.message = `Risposta da ${cmd.instruction}: ${res}`;
        }
      }

      return result;
    });
  }

  private async sendCommands(printer: Printers, commands: DitronPrinterCommand[], options?: { timeout?: number }): Promise<string> {
    if(!printer) {
      throw 'MISSING_PRINTER';
    }

    if(!printer?.ip_address) {
      throw 'MISSING_IP_ADDRESS';
    }

    const strURL = `http://${printer.ip_address}/cmd/wec`;
    const reqHeaders = {
      'Content-Type': 'text/plain'
    };
    const command = this.buildDitronPrinterCommand(commands);

    const responseObj = await this.fiscalUtilsService.sendRequest(strURL, {
        data: command,
        method: 'POST',
        headers: reqHeaders
    }).catch(err => { throw 'CONNECTION_ERROR'; });

    const rawResponse: string = responseObj.data;

    return rawResponse;
  }

  /**
   * @description Maximum number of departments supported by the Ditron printer
   */
	private getMaxDepartment (): number {
		return 40;
	}

  private async isAvailable(): Promise<string> {
    if(!this.printer) {
      throw 'MISSING_PRINTER';
    }

    try {
      const rawResponse: string = await this.sendCommands(this.printer, DITRON_SYSTEM_CMDS.GET_PRINTER_STATUS);
      const interpretedResults = this.parseDitronResponse(DITRON_SYSTEM_CMDS.GET_PRINTER_STATUS, rawResponse);

      const errorLine = interpretedResults.find(r => r.status === 'ERROR');

      if (errorLine) {
        this.handleMessageError(errorLine);
      }

      const printerStatus = findResultByCodice(interpretedResults, DITRON_SYSTEM_CMDS.GET_PRINTER_STATUS, 19);

      if(printerStatus?.data?.status !== '4')
        throw 'NOT_AVAILABLE'

      if(!this.matricola) {
        const rawResponseMatricola = await this.sendCommands(this.printer, DITRON_SYSTEM_CMDS.MATRICOLA_RT);
        const response = rawResponseMatricola.split(/\r?\n/).map(l => l.trim()).filter(l => l !== 'OK.' && l !== '');

        // never cache an error line (or nothing) as the serial number
        const errorLine = response.find(l => l.includes('ERRORE'));
        if(errorLine) {
          this.handleMessageError({ errorCode: errorLine.match(/ERRORE\s+(\d+\/\d+)/)?.[1] } as DitronCommandResult);
        }

        if(!response[0]) {
          throw 'UNKNOWN_ERROR';
        }

        this.matricola = response[0];
      }

      return "OK";
    } catch (error) {
      throw error;
    }
  }

  private handleMessageError(error: DitronCommandResult) {
    const errorCode = error.errorCode;
    if(!errorCode) {
      throw 'UNKNOWN_ERROR'
    }

    switch(errorCode) {
      case "4/224":
        throw 'FISCAL_CLOSING_NEEDED';
      case "4/106": case "4/208":
        throw 'UNKNOWN_ERROR';
      case "2/112":
        throw 'REQ_TIMEOUT';
      case "2/113":
        throw 'BUSY';
      case "4/201":
        throw 'COVER_OPEN';
      case "4/203":
        throw 'PAPER_END';
    }

    const err = errorDitronPrinter[errorCode];
    if(err) {
      throw err;
    }
      
    throw 'UNKNOWN_ERROR';
  }

  private buildHeaderCommand(sale: Sales, isRefund: boolean, hasVoidRefund: VoidRefundInterface, hasFooter: boolean): DitronPrinterCommand[] {
    let command: DitronPrinterCommand[] = [];

    // Consente insieme al comando "INP TERM=179", di poter annullare il documento commerciale (es. per fine carta) 
    // anche durante la stampa delle righe di allegato
    if(hasFooter && !isRefund) {
      command.push(...DITRON_SYSTEM_CMDS.START_ALLEG)
    }

    if (isRefund) {
      const docDataItem = sale.sale_items?.find((saleItem) => (saleItem.reference_sequential_number && saleItem.reference_date));

      if (!docDataItem) {
        throw 'MISSING_REFERENCE_DOCUMENT';
      }

      const itemsFromSameDocument = !!sale.sale_items?.every(saleItem => saleItem.reference_sequential_number === docDataItem.reference_sequential_number && saleItem.reference_date === docDataItem.reference_date);

      if (!itemsFromSameDocument) {
        throw 'ITEMS_FROM_DIFFERENT_DOCUMENTS'
      }

      const docSeqNum = this.fiscalUtilsService.parseRTDocumentSequentialNumber(docDataItem.reference_sequential_number);
      const documentDate = moment(docDataItem.reference_date).format('DDMMYY');
      const dcNum = docSeqNum.daily_closing_num.toString().padStart(4, '0');
      const seqNum = docSeqNum.document_sequential_number.toString().padStart(4, '0');

      if (hasVoidRefund.hasVoid) {
        command.push({
          type: "COMMAND",
          instruction: "DOCANNULLO",
          operands: {
            NUMSCO: seqNum,
            ZNUMBER: dcNum,
            DATASCO: documentDate,
            MATR: this.matricola,
            AUTOMATICO: ''
          }
        })
      } else {
        command.push({
          type: "COMMAND",
          instruction: "NOTACRED",
          operands: {
            ZNUMBER: dcNum,
            NUMSCO: seqNum,
            DATASCO: documentDate,
            MATR: this.matricola
          }
        })
      }
    } else {
      // Sale Name
      const printName = this.options?.print_name !== false;

      if (printName) {
        for(const row of this.fiscalUtilsService.getFiscalReceiptHeaderLines(sale)) {
          if(row) addTextLine(command, `# ${row}`);
          else addTextLine(command, " ");
        }
      }

      //Add lottery code if available
      if (sale.lottery_code) {
        command.push(...DITRON_SYSTEM_CMDS.START_LOTTERY_CODE);
        command.push({
          type: "COMMAND",
          instruction: "INP",
          operands: {
            ALFA: sale.lottery_code,
            TERM: 145
          }
        })
      }

			const customerTaxCode: string | undefined = sale.sale_customer?.tax_code || sale.customer_tax_code;

			if (customerTaxCode && this.fiscalUtilsService.checkTaxCode(customerTaxCode)) {
        command.push(...DITRON_SYSTEM_CMDS.START_CUSTOMER)
        command.push({
          type: "COMMAND",
          instruction: "INP",
          operands: {
            ALFA: customerTaxCode.toUpperCase(),
            TERM: 49
          }
        })
			}
    }
    return command;
  }

  private saleToReceiptCommand(sale: Sales, isRefund: boolean): DitronPrinterCommand[] {
    let command: DitronPrinterCommand[] = [];

    const printerMaxDepartments = this.getMaxDepartment();
    const saleItems = sale.sale_items || [];
    
    if (saleItems.some(saleItem => (saleItem.department_id || 0) > printerMaxDepartments)) {
      throw "NOT_ENOUGH_DEPARTMENTS";
    }

    const appendItems = (saleItems: SalesItems[]) => {
      command.push(...this.buildSaleItems(saleItems, isRefund));
    }

    const appendPriceChanges = (priceChanges: SalesPriceChanges[] | SalesItemsPriceChanges[]) => {
      command.push(...this.buildPriceChanges(priceChanges))
    }

    const appendSubtotal = () => {};

    this.fiscalUtilsService.applyFiscalBody(sale, appendItems, appendPriceChanges, appendSubtotal);

    return command;
  }

  private buildSaleItems(items: SalesItems[], isRefund: boolean): DitronPrinterCommand[] {
		const printNotes = this.options?.print_notes !== false;

    let orderedItems = [...items];
    const firstPositiveIndex = orderedItems.findIndex(item => Number(item.price) > 0);

    if (firstPositiveIndex > 0) {
      const zeros = orderedItems.slice(0, firstPositiveIndex);
      const rest = orderedItems.slice(firstPositiveIndex);
      orderedItems = [...rest, ...zeros];
    }

    return orderedItems.flatMap(item => {
      const cmd: DitronPrinterCommand[] = [];

      let operands: any = {
        REP: item.department?.printer_code || 1,
        PRE: Number(item.price).toFixed(2),
        DES: wrapText(item.name || '', maxLengthDesc)[0]
      };

      const isZeroPrice = Number(item.price) === 0;
      const isGift = item.price_changes?.find(change => change.type === 'gift');
      const isDepositCancellation = item.type === 'deposit_cancellation';
      const isCoupon = item.type === 'coupon';

      if(!isDepositCancellation && !isCoupon) {
        operands.QTY = Math.abs(item.quantity) || 1;
      }

      if (isZeroPrice) {
        operands.OMAGGIO = '';
      }

      if(!isRefund && item.type === 'refund') {
        operands.STORNO = '';
      }

      if(isDepositCancellation) {
        operands.STOACC = '';
      }

      if(isCoupon) {
        operands.BUOMON = '';
      }

      cmd.push({
        type: 'COMMAND',
        instruction: 'VEND',
        operands: { ...operands }
      } as DitronPrinterCommand);

      if(!isRefund) {
        if (printNotes && item.barcode) {
          if (item.barcode.toLowerCase().indexOf('p') < 0 && item.barcode.toLowerCase().indexOf('q') < 0) {
            addTextLine(cmd, item.barcode)
          }
        }

        if (printNotes && item.notes) {
          for (const noteLine of item.notes.split("\n")) {
            addTextLine(cmd, noteLine);
          }
        }

        if (item.quantity < 0 && item.refund_cause_description) {
          addTextLine(cmd, item.refund_cause_description)
        }

        if (item.quantity > 0 && item.price_changes?.length) {
          cmd.push(...this.buildPriceChanges(item.price_changes, item.department_id));
        }

        if (item.type === 'gift') {
          addTextLine(cmd, "# Omaggio")
        }
      }

      if (isGift) {
        cmd.push({
          type: 'COMMAND',
          instruction: 'VEND',
          operands: {
            ...operands,
            OMAGGIO: ''
          }
        } as DitronPrinterCommand);

        const description = `# ${isGift.description ? isGift.description : "Omaggio"}`;
        addTextLine(cmd, description);
      }
      
      return cmd;
    });
  }

  private buildPriceChanges(priceChanges: SalesPriceChanges[] | SalesItemsPriceChanges[], department_id?: number): DitronPrinterCommand[] {
    return priceChanges.flatMap(price => {
      let instructionName: InstructionName | undefined;
      let ops = {} as Record<OperandKey, string | number>;

      if(price.type.startsWith('discount_')) {
        if(price.type.endsWith("_fix")) {
          instructionName = "SCONTO";
          ops.VAL = Number(price.value).toFixed(2);
        } else {
          instructionName = "PERCA";
          ops.ALIQ = price.value;
        }
      } else if(price.type.startsWith('surcharge_')) {
        if(price.type.endsWith("_fix")) {
          instructionName = "MAGGIORA";
          ops.VAL = Number(price.value).toFixed(2);
        } else {
          instructionName = "PERCB";
          ops.ALIQ = price.value;
        }
      }

      if(!instructionName) return [];

      if(price.description) {
        ops.DES = truncateString(price.description, 44)
      }

      if(department_id == null) {
        ops.SUBT = '';
      }

      const cmd = {
        type: "COMMAND" as const,
        instruction: instructionName,
        operands: ops
      }
      return [cmd as DitronPrinterCommand];
    })
  }

  private buildPaymentCommand(sale: Sales, hasFooter: boolean, options: any): DitronPrinterCommand[] {
    let command: DitronPrinterCommand[] = [];
      
    const payments = this.fiscalUtilsService.extractPayments(sale);
    if (!payments || payments.length === 0) {
      return command;
    }

    const pay: DitronPrinterCommand[] = payments.map((pay, index) => {
      const isLast = index === payments.length - 1;
      const payment = this.getPaymentType(pay)
      const paymentCode = DITRON_PAYMENT_MAP[payment] || 1;

      const operands: any = {
        T: paymentCode
      };

      if (!isLast) {
        operands.IMP = Number(pay.amount).toFixed(2);
      }

      return {
        type: 'COMMAND',
        instruction: 'CHIUS',
        operands: operands
      };
    })

    command.push(...pay);

    if (sale.final_amount! >= 0) {
      if(hasFooter) {
        command.push(...DITRON_SYSTEM_CMDS.START_PRINT_ALLEG)
      }

      const printBarcodeId = this.options?.print_barcode_id !== false;
      const printCustomerDetail = this.options?.print_customer_detail !== false;
      const printSeller = this.options?.print_seller !== false;

      // Customer
      if (printCustomerDetail && sale.sale_customer) {
        addAttachmentLine(command, " ");
        for (const line of this.fiscalUtilsService.getCustomerInfo(sale.sale_customer)) {
          addAttachmentLine(command, line);
        }
      }

      // Print Seller
      if (printSeller && sale.seller_name) {
        addAttachmentLine(command, " ");
        addAttachmentLine(command, `${this.options?.seller_prefix || "Ti ha servito"} ${sale.seller_name.trim().split(" ")[0]}`);
      }

      // Add Tail (tail for this sale + general tail)
      let tail = options.tail || '';
  
      if (this.options?.tail) {
        addAttachmentLine(command, " ");
        tail += "\n" + this.options.tail;
      }
  
      if (tail) {
        const tails = tail.split('\n');
        for (const tRow of tails) {
          addAttachmentLine(command, tRow);
        }
      }
  
      // Payment tail */
      if (options.tail && options.tail.indexOf('FIRMA') > -1) {
        addAttachmentLine(command, " ");
        // Add Payment Tail
        for (const tRow of options.tail.split('\n')) {
          addAttachmentLine(command, tRow);
        }
      }

			// Barcode
			if (printBarcodeId && Number.isInteger(sale.id)) {
				const saleIdBarcode = sale.id!.toString();
        addAttachmentLine(command, " ");
        addBarcode(command, saleIdBarcode, true);
			}

      if(hasFooter) {
        command.push(...DITRON_SYSTEM_CMDS.END_PRINT_ALLEG)
      }
    }
    
    return command;
  }

  /**
   * @description returns the Ditron payment type from a Tilby payment
   * @param {object} payment the Tilby payment
   * @returns {string} the Ditron payment type
   */

  // [ cash, ecCard, creditCard, singlePurposeVoucher, multiPurposeVoucher, paymentProvider, deposit, noCash, none ]
  private getPaymentType(payment: FiscalPayment): DitronPayment {
    switch (payment.method_type_id) {
      case 1: case 19: case 21: case 32: case 38: case 40:
        return 'CONTANTI';
      case 39: case 2: case 22: case 23: case 28: case 29: case 36: case 41:
        return 'NON_RISCOSSO_SERVIZI'
      case 3:
        return 'ASSEGNI';
      case 4:
        return 'CREDITO_GENERICO';
      case 11: case 13: case 14: case 15: case 27: case 30: case 31: case 35: case 37: case 44: case 45: case 46: case 18: case 5:
        return 'CARTA_DI_CREDITO'
      case 24: case 43:
        return 'NON_RISCOSSO_FATTURE';
      case 25:
        return 'NON_RISCOSSO_SSN'
      case 26:
        return 'SCONTO_A_PAGARE'
      case 8:
        return 'ASSEGNI';
      case 16: case 10: case 33:
        return 'BUONI_MULTIUSO';
      case 6: case 20: case 34:
        return 'TICKET';
      default:
        return 'CONTANTI';
    }
  }

  private getNrIva(vat: Vat | undefined, configIva?: DitronPrinterCommand[]): string {
    if(!vat) return '';

    if(!vat.value) {
      const code = vat.code?.split('.')[0];
      switch(code) {
        case 'N6': //Altro non IVA
          return '0';
        case 'N4': //Esenti
          return '10';
        case 'N1': // Escluse ex art.15
         return '20';
        case 'N2': // Non soggette
          return '30';
        case 'N3': // Non imponibili
          return '40';
        case 'N5': // Regime del margine
          return '50';
        case 'VI': // Ventilazione IVA
          return '60';
      }
    }

    if (configIva) {
      const found = configIva.find(cmd => {
        if (cmd.type !== 'COMMAND' || cmd.instruction !== 'PIVA') return false;
        const ops = cmd.operands as Record<OperandKey, any>;
        return parseFloat(ops.ALIQ) === vat.value;
      });

      if (found  && found.type === 'COMMAND') {
        return (found.operands as Record<OperandKey, any>).NR.toString();
      }
    }

    return "1";
  }

  private saleToNonFiscalCommands(printer: Printers, sale: Sales, options: any) {
    const commands: DitronPrinterCommand[] = [];

    const printerColumns = printer.columns || 42;

    // Prepare tail text
    let tail = '';

    if (options.tail && typeof options.tail === 'string') {
      tail += options.tail;
    }

    if (this.options.tail && typeof this.options.tail === 'string') {
      tail += '\n' + this.options.tail;
    }

    // Use SalePrintingUtilsService to generate document lines
    const documentLines = this.salePrintingUtilsService.saleToNonFiscalDocument(sale, {
      printerColumns: printerColumns,
      tail: tail
    });

    // Process document lines
    for (const line of documentLines) {
      switch (line.type) {
        case 'text':
			    const l = cleanUpSpecialChars(line.content);
          addLineNoFiscal(commands, line.align === 'center' ? padCenter(l, printerColumns) : l);
          break;
        case 'qrcode':
          addBarcode(commands, line.content, false, barcodeTypeCodesDitronPrinter['QRCODE'])
          break;
      }
    }

    return commands;
  }

  private linesToCommands(lines: string | string[], options?: PrintFreeOptions): DitronPrinterCommand[] {
    if (!Array.isArray(lines)) {
      if (typeof lines === 'string') {
        lines = lines.split("\n");
      }
    }

    const commands: DitronPrinterCommand[] = [];

    for (const line of lines) {
      addLineNoFiscal(commands, line);
    }

    if (options?.barcode) {
      const barcodeType = barcodeTypeCodesDitronPrinter[options.barcode.type];
      addBarcode(commands, options.barcode.value, false, barcodeType);
    }

    return commands;
  }

  /**
   * saleToCourtesyReceiptCommands
   */
  private saleToCourtesyReceiptCommands(sale: Sales): DitronPrinterCommand[] {
    const lines = [];
    const courtesyReceiptOptions: PrintFreeOptions = {};

    lines.push("SCONTRINO DI CORTESIA");
    lines.push("");
    lines.push(cleanUpSpecialChars(sale.name));
    lines.push("");

    // Items
    if (Array.isArray(sale.sale_items)) {
      for (const saleItem of sale.sale_items) {
        lines.push(saleItem.quantity + "x " + cleanUpSpecialChars(saleItem.name || saleItem.department_name));
      }
    }

    if (sale.id && Number.isInteger(sale.id)) {
      courtesyReceiptOptions.barcode = {
        type: 'CODE39',
        value: sale.id.toString()
      }
    }

    return this.linesToCommands(lines, courtesyReceiptOptions);
  }


  //PUBLIC METHODS

  /**
   * @description Setup the Ditron driver
	 *
	 * @param printer, the tilby fiscal printer (must be an RCH Print!F obviously)
	 * @param options.print_notes: boolean (default true), if true prints the non-fiscal lines above each sale_item (for ex. variations, or combinations)
	 * @param options.print_name: boolean (default true), if true prints the sale.name on top
	 * @param options.print_barcode_id: boolean (default true), if true, prints a barcode on bottom that contains the sale.id
	 * @param options.ticket_change: boolean (default true), if true enables ticket change
	 * @param options.print_customer_detail: boolean (default true), if true print TAX_CODE, FIRST_NAME, LAST_NAME, COMPANY_NAME, VAT_CODE, FIDELITY
	 * @param options.print_seller: boolean (default true), if true print seller
	 * @param options.seller_prefix: string, customize seller phrase
	 * @param options.tail: string, general tail for the receipts
	 *
	 * @param printer.id: integer, the printer id
	 * @param printer.name: string, the printer name
	 * @param printer.driver: string, must be 'ditron' in this case
	 * @param printer.ip_address: string, printer IP (if using webservice mode)
	 */
  setup(printer: Printers, options: any) {
    if (!printer) {
      throw "Printer is undefined";
    }

    if (printer.driver !== 'ditron') {
      throw "Wrong driver";
    }

    if (!printer.ip_address) {
      throw "Missing ip_address";
    }

    // the service is shared by every Ditron printer: the cached serial number belongs to one device
    if (this.printer?.id !== printer.id || this.printer?.ip_address !== printer.ip_address) {
      this.matricola = '';
    }

    this.printer = printer;
    this.options = options;
  }

  async isReachable(successFunction: Function, errorFunction: Function): Promise<void> {
    if(!this.printer) {
      throw 'MISSING_PRINTER';
    }

		try {
			await this.isAvailable();
			successFunction("OK");
		} catch (error) {
			errorFunction(error);
		} 
	}

  async getPrinterStatus(options?: any): Promise<{printer_serial?: string}> {
    const result = {};
		try {
      const rawResponse: string = await this.sendCommands(this.printer, DITRON_SYSTEM_CMDS.GET_PRINTER_INFO);
      const interpretedResults = this.parseDitronResponse(DITRON_SYSTEM_CMDS.GET_PRINTER_INFO, rawResponse);

      const errorLine = interpretedResults.find(r => r.status === 'ERROR');

      if (errorLine) {
        this.handleMessageError(errorLine);
      }

      const resSerial = findResultByCodice(interpretedResults, DITRON_SYSTEM_CMDS.GET_PRINTER_INFO, 1);
      
      Object.assign(result, {
        printer_serial: resSerial?.data?.serial
      })

      return result;

    } catch(err) {
      throw err;
    }
  }

  async autoConfigurePrinter(printer: Printers, options: ItPrinterConfigurationOptions): Promise<void> {
    try { 
      await this.isAvailable();
    } catch(err) { 
      throw err; 
    }

    const resources = await this.fiscalUtilsService.getPrinterConfigurationResources();

    // Vat setup (only non-exemptions, limited to vats from id 1 to 8 since the printer can only save 8 VATs)
    const vatsToConfigure = resources.vats.filter((vat) => (vat.value != 0 && vat.code == null));

    const start = DITRON_SYSTEM_CMDS.START_PROG_CMD;
    const end = DITRON_SYSTEM_CMDS.END_PROG_CMD;
    let configIva: DitronPrinterCommand[] = [];
    let configDepartment: DitronPrinterCommand[] = [];
    let configHeaders: DitronPrinterCommand[] = [];

    if(vatsToConfigure?.length) {
      configIva = vatsToConfigure
        .slice(0, 8)
        .map((item, index) => ({
          type: 'COMMAND',
          instruction: 'PIVA',
          operands: {
            NR: index + 1,
            ALIQ: item.value || 0
          }
        }));
    }

    if(resources.departments?.length) {
      configDepartment = resources.departments
        .slice(0, 40)
        .map((dep, index) => ({
          type: 'COMMAND',
          instruction: 'PREP',
          operands: {
            NR: dep.printer_code || index + 1,
            DES: dep.name,
            IVA: this.getNrIva(dep.vat, configIva),
            LIS: '99999.00',
            PRE: 0,
            SERVIZIO: dep.sales_type === 'goods'? 'NO': 'SI',
            SCONTO: 'SI'
          }
        }))
    }

    if(options.configure_header) {
      let operandValue: string[] = [];
      // Configure headers
      for (let i = 1; i <= 6; i++) {
        const headerRow = cleanUpSpecialChars(options[`header${i}` as (`header${number}` & keyof ItPrinterConfigurationOptions)] || '');
        if(headerRow) operandValue.push(`L${i}='` + '\x1b|cA' + `${headerRow}'`);
        else operandValue.push(`L${i}='\x1b|cA'`)
      }
      configHeaders.push({
        type: 'COMMAND',
        instruction: 'PINT',
        operands: {
          ROWS: operandValue.join(',')
        }
      })
    }

    const fullSequence = [...start, ...configIva, ...configDepartment, ...configHeaders, ...end];

    try {
      const rawResponse: string = await this.sendCommands(this.printer, fullSequence);

      const interpretedResults = this.parseDitronResponse(fullSequence, rawResponse);

      const errorLine = interpretedResults.find(r => r.status === 'ERROR');

      if (errorLine) {
        this.handleMessageError(errorLine);
      }        

    } catch(err) {
      throw err;
    }

  }

  async printFiscalReceipt(sale: Sales, options: any, successFunction: Function, errorFunction: Function): Promise<void> {
    try { 
      await this.isAvailable();

      const hasTotalDiscountPerc = (sale.final_amount || 0) <= 0 && 
        (
          (sale.price_changes || []).some(price => price.type === 'discount_perc' || price.type === 'discount_fix') ||
          (sale.sale_items || []).some(item => (item.price_changes || []).some(price => price.type === 'discount_perc'  || price.type === 'discount_fix'))
        );

      if(hasTotalDiscountPerc) {
        throw "TOTAL_DISCOUNT_PERC_NOT_ALLOWED"
      }

      const isRefund = this.fiscalUtilsService.isRefundVoidSale(sale);

      let documentType = 'commercial_doc';

      if (isRefund) {
        const saleItemToCheck = sale.sale_items?.[0];

        switch (saleItemToCheck?.refund_cause_id) {
          case 6:
            documentType = "void_doc";
            break;
          default:
            documentType = "refund_doc";
        }
      } else {
        const isRefundVoid = (sale.amount || 0) <= 0 &&
          (sale.sale_items || []).some(item => item.type === 'refund');

        if(isRefundVoid) {
          throw "REFUND_NOT_ALLOWED"
        }
      }

      //Check if we are doing a void or a refund
      const hasVoidRefund: VoidRefundInterface = {
        hasVoid: false,
        hasRefund: false
      }

      for (const saleItem of (sale.sale_items || [])) {
        if (saleItem.refund_cause_id === 6) {
          hasVoidRefund.hasVoid = true;
        } else {
          hasVoidRefund.hasRefund = true;
        }
      }

      if (hasVoidRefund.hasRefund && hasVoidRefund.hasVoid) {
        throw 'MIXED_REFUND_CAUSES';
      }

      const printBarcodeId = this.options?.print_barcode_id !== false;
      const printCustomerDetail = this.options?.print_customer_detail !== false;
      const printSeller = this.options?.print_seller !== false;

      const hasFooter = printBarcodeId || printCustomerDetail || printSeller;

      const start = DITRON_SYSTEM_CMDS.START_SALE_CMD;
      const end = DITRON_SYSTEM_CMDS.END_SALE_CMD;

      const header = this.buildHeaderCommand(sale, isRefund, hasVoidRefund, hasFooter);
      const body = hasVoidRefund.hasVoid? [] : this.saleToReceiptCommand(sale, isRefund);
      const paymentAndFooter = hasVoidRefund.hasVoid? [] : this.buildPaymentCommand(sale, hasFooter, options);

      const fullSequence = [
        ...start, 
        ...header, 
        ...body, 
        ...paymentAndFooter,
        ...end
      ];

      const rawResponse: string = await this.sendCommands(this.printer, fullSequence);

      const interpretedResults = this.parseDitronResponse(fullSequence, rawResponse);

      const errorLine = interpretedResults.find(r => r.status === 'ERROR');

      if (errorLine) {
        this.handleMessageError(errorLine);
      }

			let documentData: Partial<SalesDocuments> | null = {};

      const resSerial = findResultByCodice(interpretedResults, fullSequence, 1);
      const resDate = findResultByCodice(interpretedResults, fullSequence, 7);
      const resNumbers = findResultByCodice(interpretedResults, fullSequence, 10);

      const zNumber = resNumbers?.data?.zNumber || 0;
      const numSco = resNumbers?.data?.docNumber || 0;

      if(resSerial && resNumbers) {
        Object.assign(documentData, {
          printer_serial: resSerial?.data?.serial,
          sequential_number: (zNumber * 10000) + numSco,
          date: resDate?.data?.date ? moment(resDate.data.date, 'DDMMYYHHmm').toISOString() : new Date().toISOString()
        })
        documentData.document_type = documentType;
      } else {
        documentData = null;
      }

			successFunction(documentData ? [documentData] : []);
    } catch(err) {
      errorFunction(err);
    }
  }

  async printCourtesyReceipt(sale: Sales, options: any, successFunction: Function, errorFunction: Function): Promise<void> {
    try {
      await this.isAvailable();

			const body = this.saleToCourtesyReceiptCommands(sale);

      const fullSequence = [
        ...DITRON_SYSTEM_CMDS.START_SALE_CMD,
        ...DITRON_SYSTEM_CMDS.START_NON_FISCAL_DOC,
        ...body,
        ...DITRON_SYSTEM_CMDS.END_NON_FISCAL_DOC,
        ...DITRON_SYSTEM_CMDS.END_SALE_VOID_CMD
      ];

      const rawResponse: string = await this.sendCommands(this.printer, fullSequence);

      const interpretedResults = this.parseDitronResponse(fullSequence, rawResponse);

      const errorLine = interpretedResults.find(r => r.status === 'ERROR');

      if (errorLine) {
        this.handleMessageError(errorLine);
      }

			successFunction('OK');
		} catch (error) {
			errorFunction(error);
		}
  }

  async printNonFiscal(sale: Sales, options: any, successFunction: Function, errorFunction: Function): Promise<void> {
    try {
      await this.isAvailable();

			const body = this.saleToNonFiscalCommands(this.printer, sale, options);

      const fullSequence = [
        ...DITRON_SYSTEM_CMDS.START_SALE_CMD,
        ...DITRON_SYSTEM_CMDS.START_NON_FISCAL_DOC,
        ...body,
        ...DITRON_SYSTEM_CMDS.END_NON_FISCAL_DOC,
        ...DITRON_SYSTEM_CMDS.END_SALE_VOID_CMD
      ];

      const rawResponse: string = await this.sendCommands(this.printer, fullSequence);

      const interpretedResults = this.parseDitronResponse(fullSequence, rawResponse);

      const errorLine = interpretedResults.find(r => r.status === 'ERROR');

      if (errorLine) {
        this.handleMessageError(errorLine);
      }

			successFunction('OK');
		} catch (error) {
			errorFunction(error);
		}
  }

  async printFreeNonFiscal(lines: string[], options?: PrintFreeOptions): Promise<void> {
    try {
      await this.isAvailable();

			const body = this.linesToCommands(lines, options);

      const fullSequence = [
        ...DITRON_SYSTEM_CMDS.START_SALE_CMD,
        ...DITRON_SYSTEM_CMDS.START_NON_FISCAL_DOC,
        ...body,
        ...DITRON_SYSTEM_CMDS.END_NON_FISCAL_DOC,
        ...DITRON_SYSTEM_CMDS.END_SALE_VOID_CMD
      ];

      const rawResponse: string = await this.sendCommands(this.printer, fullSequence);

      const interpretedResults = this.parseDitronResponse(fullSequence, rawResponse);

      const errorLine = interpretedResults.find(r => r.status === 'ERROR');

      if (errorLine) {
        this.handleMessageError(errorLine);
      }
		} catch (error) {
			throw(error);
		}
  }

  async dailyClosing(successFunction: Function, errorFunction: Function): Promise<void> {
		try {
      await this.isAvailable();

      const rawResponse: string = await this.sendCommands(this.printer, DITRON_SYSTEM_CMDS.AZZ_GIO_CMD);

      const interpretedResults = this.parseDitronResponse(DITRON_SYSTEM_CMDS.AZZ_GIO_CMD, rawResponse);

      const errorLine = interpretedResults.find(r => r.status === 'ERROR');

      if (errorLine) {
        this.handleMessageError(errorLine);
      }
      successFunction("OK");
    } catch(err) {
      errorFunction(err);
    }
  }

  async dailyRead(): Promise<void> {
		try {
      await this.isAvailable();

      const rawResponse: string = await this.sendCommands(this.printer, DITRON_SYSTEM_CMDS.REA_GIO_CMD);

      const interpretedResults = this.parseDitronResponse(DITRON_SYSTEM_CMDS.REA_GIO_CMD, rawResponse);

      const errorLine = interpretedResults.find(r => r.status === 'ERROR');

      if (errorLine) {
        this.handleMessageError(errorLine);
      }
    } catch(err) {
      throw(err);
    }
  }

  async openCashDrawer(successFunction: Function, errorFunction: Function): Promise<void> {
    try {
      await this.isAvailable();

      const rawResponse: string = await this.sendCommands(this.printer, DITRON_SYSTEM_CMDS.OPEN_CASH_DRAWER);

      const interpretedResults = this.parseDitronResponse(DITRON_SYSTEM_CMDS.OPEN_CASH_DRAWER, rawResponse);

      const errorLine = interpretedResults.find(r => r.status === 'ERROR');

      if (errorLine) {
        this.handleMessageError(errorLine);
      }

			successFunction('OK');
		} catch (error) {
			errorFunction(error);
		}
  }

  async displayText(printer: Printers, textLines: string[]): Promise<void> {
    try {
      await this.isAvailable();

      let body: DitronPrinterCommand[] = [];

      (textLines || []).forEach((line, index) => {
        if(index <= 1) {
          const operands = index === 0 ?
            { CLI1: line } : { CLI2: line }
          body.push({
            type: 'COMMAND',
            instruction: "VIS",
            operands: operands
          })
        }
      })

      if(body.length) {
        const fullSequence = [
          ...DITRON_SYSTEM_CMDS.START_CMD,
          ...body,
          ...DITRON_SYSTEM_CMDS.END_CMD
        ];
  
        const rawResponse: string = await this.sendCommands(this.printer, fullSequence);
  
        const interpretedResults = this.parseDitronResponse(fullSequence, rawResponse);
  
        const errorLine = interpretedResults.find(r => r.status === 'ERROR');
  
        if (errorLine) {
          this.handleMessageError(errorLine);
        }
      }
		} catch (error) {
			throw(error);
		}
  }
}
